import { computed, ref, watch } from 'vue';
import PendingMarketUpdatesAPI from 'dashboard/api/pendingMarketUpdates';
import { useAlert } from 'dashboard/composables';
import {
  MAX_UPLOAD_IMAGE_SIZE,
  REGISTRATION_STEPS,
} from '../utils/pendentesConstants';
import {
  optionLabel,
  propertySubtitle,
  propertyTitle,
} from '../utils/pendentesFormatters';
import {
  defaultRegistrationFields,
  defaultRegistrationOptions,
  normalizeMediaImages,
  normalizeRegistrationOptions,
  registrationStepKeyForFieldError,
  reviewRegistrationFields,
  sanitizeRegistrationFields,
  validateRegistrationFields,
} from '../utils/pendentesRegistration';
import {
  allocationErrorsFor,
  buildAllocationPreview,
  buildAutoAllocationPreview,
  normalizeOptionistSearchValue,
  optionistMatchesPendingItem,
  phonesMatch,
} from '../utils/pendentesOptionists';

export function usePendentesLaudoRegistration({
  currentUser,
  actorPayload,
  registeringLaudoItemId,
  closeDialog,
  afterRegister,
}) {
  const registerTargetItem = ref(null);
  const registerFields = ref(defaultRegistrationFields());
  const registerOptions = ref(defaultRegistrationOptions());
  const isLoadingRegistrationPreview = ref(false);
  const registrationPreviewError = ref('');
  const activeMediaImageIndex = ref(0);
  const activeRegistrationStep = ref(REGISTRATION_STEPS[0].key);
  const isReadingMediaFiles = ref(false);
  const optionists = ref([]);
  const optionistSearch = ref('');
  const isLoadingOptionists = ref(false);
  const selectedPrimaryOptionistId = ref('');
  const selectedExtraOptionistIds = ref([]);
  const manualOptionistPercentages = ref({});

  const isRegisteringLaudo = computed(() =>
    Boolean(registeringLaudoItemId.value)
  );

  function ensureMediaImageIndex() {
    const total = registerFields.value.mediaImages?.length || 0;
    if (!total) {
      activeMediaImageIndex.value = 0;
      return;
    }
    activeMediaImageIndex.value = Math.min(
      activeMediaImageIndex.value,
      total - 1
    );
  }

  function selectMediaImage(index) {
    activeMediaImageIndex.value = Math.max(
      0,
      Math.min(index, (registerFields.value.mediaImages?.length || 1) - 1)
    );
  }

  function nextMediaImage() {
    const total = registerFields.value.mediaImages?.length || 0;
    if (!total) return;
    activeMediaImageIndex.value = (activeMediaImageIndex.value + 1) % total;
  }

  function previousMediaImage() {
    const total = registerFields.value.mediaImages?.length || 0;
    if (!total) return;
    activeMediaImageIndex.value =
      (activeMediaImageIndex.value - 1 + total) % total;
  }

  function removeMediaImage(index) {
    registerFields.value.mediaImages = normalizeMediaImages(
      registerFields.value.mediaImages.filter(
        (_, itemIndex) => itemIndex !== index
      )
    );
    ensureMediaImageIndex();
  }

  function readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () =>
        reject(reader.error || new Error('Falha ao ler imagem.'));
      reader.readAsDataURL(file);
    });
  }

  async function addMediaImagesFromFiles(event) {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    const validFiles = [];
    files.forEach(file => {
      if (!/^image\/jpe?g$/i.test(file.type) && !/\.jpe?g$/i.test(file.name)) {
        useAlert(`${file.name} não é JPG/JPEG.`);
        return;
      }
      if (file.size > MAX_UPLOAD_IMAGE_SIZE) {
        useAlert(`${file.name} ultrapassa 8 MB.`);
        return;
      }
      validFiles.push(file);
    });
    if (!validFiles.length) return;

    isReadingMediaFiles.value = true;
    try {
      const uploadedImages = await Promise.all(
        validFiles.map(async (file, index) => {
          const dataUrl = await readFileAsDataUrl(file);
          return {
            id: `upload-${Date.now()}-${index}`,
            url: dataUrl,
            dataUrl,
            filename: file.name,
            name: file.name,
            mimeType: file.type || 'image/jpeg',
            size: file.size,
            caption: file.name.replace(/\.[^.]+$/, ''),
            source: 'upload',
          };
        })
      );
      registerFields.value.mediaImages = normalizeMediaImages([
        ...(registerFields.value.mediaImages || []),
        ...uploadedImages,
      ]);
      selectMediaImage(
        Math.max(
          0,
          registerFields.value.mediaImages.length - uploadedImages.length
        )
      );
    } finally {
      isReadingMediaFiles.value = false;
    }
  }

  function updateActiveMediaImageCaption(value) {
    const images = [...(registerFields.value.mediaImages || [])];
    if (!images[activeMediaImageIndex.value]) return;
    images[activeMediaImageIndex.value] = {
      ...images[activeMediaImageIndex.value],
      caption: value,
    };
    registerFields.value.mediaImages = images;
  }

  const registerTargetTitle = computed(() =>
    registerTargetItem.value
      ? propertyTitle(registerTargetItem.value)
      : 'imóvel'
  );
  const registerTargetSubtitle = computed(() =>
    registerTargetItem.value ? propertySubtitle(registerTargetItem.value) : ''
  );
  const registrationMediaImages = computed(() =>
    normalizeMediaImages(registerFields.value.mediaImages)
  );
  const activeMediaImage = computed(
    () => registrationMediaImages.value[activeMediaImageIndex.value] || null
  );
  const registerSourceUrl = computed(
    () =>
      registerTargetItem.value?.sourceUrl ||
      registerFields.value.sourceUrl ||
      ''
  );
  const registerSourcePortal = computed(
    () =>
      registerTargetItem.value?.sourcePortal ||
      registerFields.value.sourcePortal ||
      registerTargetItem.value?.sourceKind ||
      ''
  );
  const mediaImageCounterLabel = computed(() => {
    const total = registrationMediaImages.value.length;
    if (!total) return '0 fotos';
    return `${activeMediaImageIndex.value + 1} de ${total}`;
  });
  const registrationSteps = computed(() => REGISTRATION_STEPS);
  const activeRegistrationStepIndex = computed(() =>
    Math.max(
      0,
      REGISTRATION_STEPS.findIndex(
        step => step.key === activeRegistrationStep.value
      )
    )
  );
  const activeRegistrationStepConfig = computed(
    () => REGISTRATION_STEPS[activeRegistrationStepIndex.value]
  );
  const isFirstRegistrationStep = computed(
    () => activeRegistrationStepIndex.value === 0
  );
  const isLastRegistrationStep = computed(
    () => activeRegistrationStepIndex.value === REGISTRATION_STEPS.length - 1
  );
  const mappedOptionists = computed(() =>
    optionists.value.filter(optionist => optionist.canRegister)
  );
  const marlonOptionist = computed(
    () => optionists.value.find(optionist => optionist.isMarlon) || null
  );
  const primaryOptionistOptions = computed(() =>
    mappedOptionists.value.filter(optionist => !optionist.isMarlon)
  );
  const selectedPrimaryOptionist = computed(
    () =>
      optionists.value.find(
        optionist =>
          String(optionist.externalUserId) ===
          String(selectedPrimaryOptionistId.value)
      ) || null
  );
  const filteredExtraOptionists = computed(() => {
    const query = optionistSearch.value.trim().toLowerCase();
    return optionists.value
      .filter(optionist => !optionist.isMarlon)
      .filter(
        optionist =>
          String(optionist.externalUserId) !==
          String(selectedPrimaryOptionistId.value)
      )
      .filter(optionist => {
        if (!query) return true;
        return [optionist.name, optionist.email, optionist.externalStoreName]
          .filter(Boolean)
          .some(value => String(value).toLowerCase().includes(query));
      });
  });
  const selectedRegistrationIds = computed(() => {
    const ids = [];
    if (selectedPrimaryOptionistId.value) {
      ids.push(Number(selectedPrimaryOptionistId.value));
    }
    selectedExtraOptionistIds.value.forEach(id => {
      const numericId = Number(id);
      if (!ids.includes(numericId)) ids.push(numericId);
    });
    return ids;
  });
  const optionistAllocationPreview = computed(() =>
    buildAllocationPreview(
      selectedRegistrationIds.value,
      optionists.value,
      marlonOptionist.value,
      manualOptionistPercentages.value
    )
  );
  const optionistAllocationErrors = computed(() =>
    allocationErrorsFor(optionistAllocationPreview.value)
  );
  const visibleOptionistAllocationErrors = computed(() =>
    optionistAllocationErrors.value.slice(0, 3)
  );

  function currentUserCustomAttributes() {
    const user = currentUser.value || {};
    return user.custom_attributes || user.customAttributes || {};
  }

  function currentUserIdCandidates() {
    const attrs = currentUserCustomAttributes();
    return [
      attrs.midas_user_id,
      attrs.midasUserId,
      attrs.midas_external_user_id,
      attrs.midasExternalUserId,
      attrs.external_user_id,
      attrs.externalUserId,
    ]
      .filter(value => value !== undefined && value !== null && value !== '')
      .map(value => String(value));
  }

  function currentUserNameCandidates() {
    const user = currentUser.value || {};
    return [
      user.available_name,
      user.availableName,
      user.display_name,
      user.displayName,
      user.name,
    ]
      .map(normalizeOptionistSearchValue)
      .filter(Boolean);
  }

  function currentUserEmailCandidates() {
    const user = currentUser.value || {};
    return [user.email]
      .map(value =>
        String(value || '')
          .trim()
          .toLowerCase()
      )
      .filter(Boolean);
  }

  function currentUserPhoneCandidates() {
    const user = currentUser.value || {};
    const attrs = currentUserCustomAttributes();
    return [
      user.phone_number,
      user.phoneNumber,
      user.phone,
      attrs.phone_number,
      attrs.phoneNumber,
      attrs.phone,
      attrs.mobile,
      attrs.telefone,
      attrs.whatsapp,
    ].filter(Boolean);
  }

  function setPrimaryOptionist(optionist) {
    if (!optionist?.externalUserId) return false;
    selectedPrimaryOptionistId.value = Number(optionist.externalUserId);
    selectedExtraOptionistIds.value = selectedExtraOptionistIds.value.filter(
      id => String(id) !== String(optionist.externalUserId)
    );
    return true;
  }

  function optionistMatchesCurrentPublisher(optionist) {
    if (!optionist?.canRegister || optionist.isMarlon) return false;

    const idCandidates = currentUserIdCandidates();
    if (
      idCandidates.length &&
      idCandidates.includes(String(optionist.externalUserId))
    ) {
      return true;
    }

    const optionistEmail = String(optionist.email || '')
      .trim()
      .toLowerCase();
    if (
      optionistEmail &&
      currentUserEmailCandidates().includes(optionistEmail)
    ) {
      return true;
    }

    if (
      currentUserPhoneCandidates().some(phone =>
        phonesMatch(optionist.phone, phone)
      )
    ) {
      return true;
    }

    const optionistName = normalizeOptionistSearchValue(optionist.name);
    if (!optionistName) return false;
    return currentUserNameCandidates().some(
      name =>
        name === optionistName ||
        (name.length >= 6 &&
          optionistName.length >= 6 &&
          (name.includes(optionistName) || optionistName.includes(name)))
    );
  }

  function autoSelectPrimaryOptionistFromPublisher() {
    if (selectedPrimaryOptionistId.value) return false;
    const match = primaryOptionistOptions.value.find(optionist =>
      optionistMatchesCurrentPublisher(optionist)
    );
    return setPrimaryOptionist(match);
  }

  function autoSelectPrimaryOptionistFromPending(
    item = registerTargetItem.value
  ) {
    if (selectedPrimaryOptionistId.value) return false;
    const match = primaryOptionistOptions.value.find(optionist =>
      optionistMatchesPendingItem(optionist, item)
    );
    return setPrimaryOptionist(match);
  }

  function promoteFirstExtraOptionistToPrimary() {
    if (selectedPrimaryOptionistId.value) return false;
    const selectedExtraIds = selectedExtraOptionistIds.value.map(String);
    const match = selectedExtraIds
      .map(selectedId =>
        primaryOptionistOptions.value.find(
          optionist => String(optionist.externalUserId) === selectedId
        )
      )
      .find(Boolean);
    return setPrimaryOptionist(match);
  }

  function autoSelectPrimaryOptionist(item = registerTargetItem.value) {
    return (
      autoSelectPrimaryOptionistFromPublisher() ||
      autoSelectPrimaryOptionistFromPending(item) ||
      promoteFirstExtraOptionistToPrimary()
    );
  }

  const normalizedOptionistAllocations = computed(() =>
    optionistAllocationPreview.value.map(row => ({
      externalUserId: Number(row.optionist.externalUserId),
      percentage: Number(row.percentage || 0).toFixed(2),
    }))
  );
  const normalizedRegistrationFields = computed(() => {
    const fields = { ...registerFields.value };
    fields.propertyTypeLabel = optionLabel(
      registerOptions.value.propertyTypes,
      fields.propertyTypeId,
      fields.propertyTypeLabel
    );
    fields.mediaDisclosureLabel = optionLabel(
      registerOptions.value.mediaDisclosures,
      fields.mediaDisclosureId,
      fields.mediaDisclosureLabel
    );
    fields.contactFormLabel = optionLabel(
      registerOptions.value.contactForms,
      fields.contactFormId,
      fields.contactFormLabel
    );
    fields.saleStatusLabel = optionLabel(
      registerOptions.value.saleStatuses,
      fields.saleStatusId,
      fields.saleStatusLabel
    );
    fields.optionTypeLabel = optionLabel(
      registerOptions.value.optionTypes,
      fields.optionTypeId,
      fields.optionTypeLabel
    );
    fields.complementTypeLabel = optionLabel(
      registerOptions.value.complementTypes,
      fields.complementTypeId,
      fields.complementTypeLabel
    );
    fields.complement2TypeLabel = optionLabel(
      registerOptions.value.complementTypes,
      fields.complement2TypeId,
      fields.complement2TypeLabel
    );
    fields.mediaImages = registrationMediaImages.value.map((image, index) => ({
      ...image,
      position: index + 1,
    }));
    return sanitizeRegistrationFields(fields);
  });
  const registrationFieldErrors = computed(() =>
    validateRegistrationFields(registerFields.value)
  );
  const visibleRegistrationErrors = computed(() =>
    registrationFieldErrors.value.slice(0, 3)
  );
  const canConfirmLaudoRegistration = computed(
    () =>
      Boolean(registerTargetItem.value) &&
      Boolean(selectedPrimaryOptionistId.value) &&
      Boolean(marlonOptionist.value?.canRegister) &&
      !registrationFieldErrors.value.length &&
      !optionistAllocationErrors.value.length &&
      !isLoadingRegistrationPreview.value &&
      !isLoadingOptionists.value &&
      !isRegisteringLaudo.value
  );
  const registrationStepErrors = computed(() => {
    const errorsByStep = REGISTRATION_STEPS.reduce(
      (accumulator, step) => ({ ...accumulator, [step.key]: [] }),
      {}
    );

    registrationFieldErrors.value.forEach(error => {
      errorsByStep[registrationStepKeyForFieldError(error)].push(error);
    });

    if (!selectedPrimaryOptionistId.value) {
      errorsByStep.team.push('Escolha o corretor principal.');
    }
    if (!isLoadingOptionists.value && !marlonOptionist.value?.canRegister) {
      errorsByStep.team.push('Marlon Reis precisa estar mapeado no Midas.');
    }
    optionistAllocationErrors.value.forEach(error => {
      errorsByStep.team.push(error);
    });

    return errorsByStep;
  });
  const activeRegistrationStepErrors = computed(
    () => registrationStepErrors.value[activeRegistrationStep.value] || []
  );
  const firstRegistrationIssueStep = computed(
    () =>
      REGISTRATION_STEPS.find(
        step => registrationStepErrors.value[step.key]?.length
      )?.key || ''
  );
  const registrationReviewCards = computed(() => [
    {
      key: 'media',
      step: 'media',
      label: 'Fotos',
      value: registrationMediaImages.value.length
        ? `${registrationMediaImages.value.length} selecionada(s)`
        : 'Sem fotos',
      icon: 'i-lucide-images',
      tone: registrationMediaImages.value.length
        ? 'bg-woot-50 text-woot-700 ring-woot-100'
        : 'bg-slate-100 text-slate-600 ring-slate-200',
    },
    {
      key: 'price',
      step: 'pricing',
      label: 'Valor',
      value: registerFields.value.salePrice || '-',
      icon: 'i-lucide-badge-dollar-sign',
      tone: registerFields.value.salePrice
        ? 'bg-green-50 text-green-700 ring-green-100'
        : 'bg-yellow-50 text-yellow-800 ring-yellow-100',
    },
    {
      key: 'contact',
      step: 'identity',
      label: 'Contato',
      value:
        registerFields.value.ownerPhone ||
        registerFields.value.ownerEmail ||
        'Pendente',
      icon: 'i-lucide-phone',
      tone:
        registerFields.value.ownerPhone || registerFields.value.ownerEmail
          ? 'bg-violet-50 text-violet-700 ring-violet-100'
          : 'bg-yellow-50 text-yellow-800 ring-yellow-100',
    },
    {
      key: 'team',
      step: 'team',
      label: 'Time',
      value: optionistAllocationPreview.value.length
        ? `${optionistAllocationPreview.value.length} opcionista(s)`
        : 'Escolher corretor',
      icon: 'i-lucide-users-round',
      tone: optionistAllocationPreview.value.length
        ? 'bg-lais-primary-50 text-lais-primary ring-lais-primary-50'
        : 'bg-yellow-50 text-yellow-800 ring-yellow-100',
    },
  ]);

  function setRegistrationStep(stepKey) {
    if (REGISTRATION_STEPS.some(step => step.key === stepKey)) {
      activeRegistrationStep.value = stepKey;
    }
  }

  function goToNextRegistrationStep() {
    const nextStep = REGISTRATION_STEPS[activeRegistrationStepIndex.value + 1];
    if (nextStep) setRegistrationStep(nextStep.key);
  }

  function goToPreviousRegistrationStep() {
    const previousStep =
      REGISTRATION_STEPS[activeRegistrationStepIndex.value - 1];
    if (previousStep) setRegistrationStep(previousStep.key);
  }

  function goToFirstRegistrationIssue() {
    if (firstRegistrationIssueStep.value) {
      setRegistrationStep(firstRegistrationIssueStep.value);
    }
  }

  function isExtraSelected(optionist) {
    return selectedExtraOptionistIds.value
      .map(String)
      .includes(String(optionist.externalUserId));
  }

  function toggleExtraOptionist(optionist) {
    if (!optionist.canRegister) return;
    const id = Number(optionist.externalUserId);
    const selected = selectedExtraOptionistIds.value.map(Number);
    if (selected.includes(id)) {
      selectedExtraOptionistIds.value = selected.filter(value => value !== id);
      return;
    }
    if (selectedRegistrationIds.value.length >= 3) {
      useAlert(
        'O Midas aceita ate 4 opcionistas. Marlon entra automaticamente, entao selecione ate 3 corretores.'
      );
      return;
    }
    selectedExtraOptionistIds.value = [...selected, id];
    promoteFirstExtraOptionistToPrimary();
  }

  function syncOptionistPercentages() {
    const autoRows = buildAutoAllocationPreview(
      selectedRegistrationIds.value,
      optionists.value,
      marlonOptionist.value
    );
    const next = {};
    autoRows
      .filter(row => !row.optionist.isMarlon)
      .forEach(row => {
        const id = String(row.optionist.externalUserId);
        next[id] =
          manualOptionistPercentages.value[id] === undefined
            ? Number(row.percentage).toFixed(2)
            : manualOptionistPercentages.value[id];
      });
    manualOptionistPercentages.value = next;
  }

  function setOptionistPercentage(optionistId, value) {
    manualOptionistPercentages.value = {
      ...manualOptionistPercentages.value,
      [String(optionistId)]: value,
    };
  }

  function isOptionistPercentageEditable(row) {
    return (
      !row.optionist.isMarlon &&
      optionistAllocationPreview.value.filter(item => !item.optionist.isMarlon)
        .length > 1
    );
  }

  async function loadOptionists() {
    if (optionists.value.length || isLoadingOptionists.value) return;
    isLoadingOptionists.value = true;
    try {
      const { data } = await PendingMarketUpdatesAPI.midasOptionists();
      optionists.value = data.items || [];
      autoSelectPrimaryOptionist();
    } catch (error) {
      useAlert(
        error.response?.data?.error ||
          error.response?.data?.details?.detail ||
          'Nao foi possivel carregar os opcionistas do Midas.'
      );
    } finally {
      isLoadingOptionists.value = false;
    }
  }

  async function loadRegistrationPreview(item) {
    if (!item?.id) return;
    isLoadingRegistrationPreview.value = true;
    registrationPreviewError.value = '';
    try {
      const { data } = await PendingMarketUpdatesAPI.midasLaudoPreview(
        item.id,
        actorPayload.value
      );
      registerFields.value = reviewRegistrationFields(
        {
          ...defaultRegistrationFields(item),
          ...(data.fields || {}),
          mediaImages: normalizeMediaImages(data.fields?.mediaImages || []),
        },
        item
      );
      ensureMediaImageIndex();
      registerOptions.value = normalizeRegistrationOptions(data.options || {});
      const serverErrors = data.validation?.errors || [];
      if (serverErrors.length) {
        registrationPreviewError.value = serverErrors.join(' ');
      }
    } catch (error) {
      registrationPreviewError.value =
        error.response?.data?.error ||
        error.response?.data?.details?.detail ||
        'Nao foi possivel carregar a ficha do imóvel no Midas.';
      useAlert(registrationPreviewError.value);
    } finally {
      isLoadingRegistrationPreview.value = false;
    }
  }

  function prepareRegistration(item) {
    registerTargetItem.value = item;
    registerFields.value = defaultRegistrationFields(item);
    registerOptions.value = defaultRegistrationOptions();
    registrationPreviewError.value = '';
    selectedPrimaryOptionistId.value = '';
    selectedExtraOptionistIds.value = [];
    manualOptionistPercentages.value = {};
    optionistSearch.value = '';
    activeMediaImageIndex.value = 0;
    activeRegistrationStep.value = REGISTRATION_STEPS[0].key;
  }

  async function loadRegistration(item) {
    await Promise.all([loadOptionists(), loadRegistrationPreview(item)]);
    autoSelectPrimaryOptionist(item);
  }

  function resetRegistration() {
    if (isRegisteringLaudo.value) return;
    registerTargetItem.value = null;
    registerFields.value = defaultRegistrationFields();
    registrationPreviewError.value = '';
    manualOptionistPercentages.value = {};
    activeMediaImageIndex.value = 0;
    activeRegistrationStep.value = REGISTRATION_STEPS[0].key;
  }

  async function confirmLaudoRegistration() {
    if (!canConfirmLaudoRegistration.value || !registerTargetItem.value) return;
    const item = registerTargetItem.value;
    registeringLaudoItemId.value = String(item.id);
    try {
      const { data } = await PendingMarketUpdatesAPI.registerLaudoMidas(
        item.id,
        {
          ...actorPayload.value,
          optionistIds: selectedRegistrationIds.value,
          optionistAllocations: normalizedOptionistAllocations.value,
          propertyFields: normalizedRegistrationFields.value,
        }
      );
      useAlert(data?.message || 'Cadastro de laudo iniciado no Midas.');
      closeDialog();
      registerTargetItem.value = null;
      await afterRegister({ item, data });
    } catch (error) {
      useAlert(
        error.response?.data?.error ||
          error.response?.data?.details?.detail ||
          'Nao foi possivel cadastrar o laudo no Midas.'
      );
    } finally {
      registeringLaudoItemId.value = '';
    }
  }

  watch(selectedRegistrationIds, syncOptionistPercentages);
  watch(optionists, syncOptionistPercentages);
  watch(currentUser, () => {
    if (registerTargetItem.value && !selectedPrimaryOptionistId.value) {
      autoSelectPrimaryOptionist();
    }
  });

  return {
    registerTargetItem,
    registerFields,
    registerOptions,
    isLoadingRegistrationPreview,
    registrationPreviewError,
    activeMediaImageIndex,
    activeRegistrationStep,
    isReadingMediaFiles,
    optionistSearch,
    isLoadingOptionists,
    selectedPrimaryOptionistId,
    manualOptionistPercentages,
    isRegisteringLaudo,
    registerTargetTitle,
    registerTargetSubtitle,
    registrationMediaImages,
    activeMediaImage,
    registerSourceUrl,
    registerSourcePortal,
    mediaImageCounterLabel,
    registrationSteps,
    activeRegistrationStepIndex,
    activeRegistrationStepConfig,
    isFirstRegistrationStep,
    isLastRegistrationStep,
    primaryOptionistOptions,
    selectedPrimaryOptionist,
    filteredExtraOptionists,
    optionistAllocationPreview,
    optionistAllocationErrors,
    visibleOptionistAllocationErrors,
    registrationFieldErrors,
    visibleRegistrationErrors,
    canConfirmLaudoRegistration,
    registrationStepErrors,
    activeRegistrationStepErrors,
    firstRegistrationIssueStep,
    registrationReviewCards,
    selectMediaImage,
    nextMediaImage,
    previousMediaImage,
    removeMediaImage,
    addMediaImagesFromFiles,
    updateActiveMediaImageCaption,
    setRegistrationStep,
    goToNextRegistrationStep,
    goToPreviousRegistrationStep,
    goToFirstRegistrationIssue,
    isExtraSelected,
    toggleExtraOptionist,
    setOptionistPercentage,
    isOptionistPercentageEditable,
    prepareRegistration,
    loadRegistration,
    resetRegistration,
    confirmLaudoRegistration,
  };
}
